<template>
  <global-modal name="event-modal-staff-passes" @before-open="beforeOpen">
    <Modal v-if="event" @close="$modal.hide('event-modal-staff-passes')">
      <EventModalPassesHeader
        :options="header"
        @download="downloadAllPasses"
        @print="printAllPasses"
        :hide-quota="
          Number(getLocaleValue(exhibition, 'create_staff_pass_disabled'))
        "
      >
        <template slot="title">
          {{ $t("event.passes.staffPasses") }}
        </template>

        <template
          slot="deadlines-text"
          v-if="
            installationStartDate &&
              installationEndDate &&
              dismantingStartDate &&
              dismantingEndDate
          "
        >
          {{
            $t("event.passes.montage.installationPeriod", {
              from: installationStartDate.format("DD.MM.YYYY"),
              to: installationEndDate.format("DD.MM.YYYY")
            })
          }}

          <br />

          {{
            $t("event.passes.montage.dismantingPeriod", {
              from: dismantingStartDate.format("DD.MM.YYYY"),
              to: dismantingEndDate.format("DD.MM.YYYY")
            })
          }}
        </template>
        <template slot="pass-warning">
          <span v-html="getLocaleValue(exhibition, 'staff_pass_info_1')"></span>
        </template>
        <template
          v-if="getLocaleValue(exhibition, 'staff_pass_info_2').length"
          slot="pass-warning2"
        >
          <span v-html="getLocaleValue(exhibition, 'staff_pass_info_2')"></span>
        </template>
        <template slot="quota-text">
          {{ $t("event.passes.staff.quotaText") }}
        </template>
        <template
          v-if="
            passDeadline.startStaffPrint &&
              secondsFromDeadlineStaffPrint >= 0 &&
              header.quota.total
          "
          slot="warning-message"
        >
          <icon name="warning--no-color" />
          {{
            $t("event.passes.staff.warningMessage", {
              date: passDeadline.startStaffPrint.format("DD.MM.YYYY")
            })
          }}
        </template>
        <template slot="pass_terms">
          <div v-html="getLocaleValue(exhibition, 'staff_pass_info_3')"></div>
          <br />
          <LinkIcon
            v-if="getLocaleValue(exhibition, 'staff_pass_terms').length"
            icon="doc"
            bold
            grey
            :href="
              exhibition.website +
                getLocaleValue(exhibition, 'staff_pass_terms')
            "
            target="_blank"
          >
            {{ $t("event.passes.car.appForms") }}
          </LinkIcon>
        </template>
      </EventModalPassesHeader>
      <div class="loader-wrap" v-if="isLoading">
        <Loader is-absolute white opacity />
      </div>
      <template
        v-else-if="
          !Number(getLocaleValue(exhibition, 'create_staff_pass_disabled'))
        "
      >
        <EventModalPassesAddForm
          v-show="
            hasAccessToWrite &&
              (isAddFormShown || !staffPasses.length) &&
              diffInSeconds(passDeadline.staff) >= 0 &&
              !isQuotaFull
          "
          :is-submitting="isAddFormSubmitting"
          :show-hide="!!staffPasses.length"
          package-upload
          :package-upload-template-src="$t('event.passes.staff.downloadBlank')"
          :package-upload-template-size="
            $t('event.passes.staff.packageUploadSize')
          "
          :has-errors="errors.any()"
          @package-upload-handler="uploadPackage"
          @submit="submit"
          @hide="isAddFormShown = false"
          class="event-modal-staff-passes__add"
        >
          <template slot="form-title">
            {{ $t("event.passes.staff.addTitle") }}
          </template>

          <div class="event-modal-passes-add-form__title">
            {{ $t("event.passes.staff.applicationTitle") }}
          </div>

          <div class="event-modal-passes-add-form__row flex">
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("event.passes.staff.companyName") }}
                </template>

                <input
                  v-model="companyName"
                  v-validate="'required'"
                  type="text"
                  name="companyName"
                />

                <template v-if="errors.has('companyName')" slot="error">
                  {{ errors.first("companyName") }}
                </template>
              </InputLabel>
            </div>
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("common.lastName") }}
                </template>

                <input
                  v-model="lastName"
                  v-validate="'required|custom_alpha|min:1|max:50'"
                  type="text"
                  name="lastName"
                />

                <template v-if="errors.has('lastName')" slot="error">
                  {{ errors.first("lastName") }}
                </template>
              </InputLabel>
            </div>
          </div>

          <div class="event-modal-passes-add-form__row flex">
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("common.firstName") }}
                </template>

                <input
                  v-model="firstName"
                  v-validate="'required|custom_alpha|min:1|max:50'"
                  type="text"
                  name="firstName"
                />

                <template v-if="errors.has('firstName')" slot="error">
                  {{ errors.first("firstName") }}
                </template>
              </InputLabel>
            </div>
            <div class="event-modal-passes-add-form__row-col _380">
              <InputLabel>
                <template #label>
                  {{ $t("common.middleName") }}
                </template>

                <input
                  name="middleName"
                  v-model="middleName"
                  type="text"
                  v-validate="'custom_alpha|min:1|max:50'"
                />

                <template v-if="errors.has('middleName')" slot="error">
                  {{ errors.first("middleName") }}
                </template>
              </InputLabel>
            </div>
          </div>
        </EventModalPassesAddForm>

        <EventModalPassesUsers
          type="staff"
          :users="staffPasses"
          :deadline="passDeadline.staff"
          :deadlineStartPrint="passDeadline.startStaffPrint"
          :deadlineEndPrint="passDeadline.endStaffPrint"
          :allow-adding-pass="!isQuotaFull"
          :is-loading="isLoadingWithOpacity"
          :sorting="staffPassSorting"
          @add="addNewPass"
          @download="downloadPass"
          @print="printPass"
          @delete="deletePass"
          @delete-all="deleteAllPasses"
          @upload="sortingPasses"
          @sortPasses="sortingPasses"
          class="event-modal-staff-passes__users"
        />
        <div class="event-modal-staff-passes__users">
          <pagination
            class="event-modal-staff-passes__pagination"
            v-if="staffPassesPaginationLength > 1"
            :page="currentPage"
            :page-count="staffPassesPaginationLength"
            :total-visible="paginationPageAmount"
            @change="paginate"
          />
        </div>
      </template>
    </Modal>
  </global-modal>
</template>

<script>
import Modal from "@/components/Modal/Modal.vue";
import EventModalPassesHeader from "./EventModalPassesHeader.vue";
import EventModalPassesAddForm from "./EventModalPassesAddForm.vue";
import EventModalPassesUsers from "./EventModalPassesUsers.vue";

import moment from "moment";
import { mapState, mapGetters } from "vuex";

import diffInSeconds from "@/helpers/diffInSeconds";

import apiPersonalPass from "@/api/personalPass";

import { Loader, Pagination, InputLabel } from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";
import printJS from "print-js";
import getError from "@/helpers/error";

import { getLocaleValue } from "@/helpers/common";
import LinkIcon from "@v2/shared/ui/components/icons/LinkIcon/LinkIcon.vue";

export default {
  name: "EventModalStaffPasses",

  components: {
    Pagination,
    Loader,
    Modal,
    InputLabel,
    LinkIcon,

    EventModalPassesHeader,
    EventModalPassesAddForm,
    EventModalPassesUsers
  },

  data: () => ({
    isLoading: false,
    isLoadingWithOpacity: false,
    isAddFormShown: false,
    isAddFormSubmitting: false,
    companyName: "",
    lastName: "",
    firstName: "",
    middleName: "",
    currentPage: 1
  }),

  computed: {
    ...mapState("event", ["event"]),
    ...mapState("passes", [
      "staffPasses",
      "staffPassesPaginationLength",
      "staffPassSorting",
      "staffPassesAmount"
    ]),
    ...mapGetters("event", [
      "exhibition",
      "hasAccessToWrite",
      "eventId",
      "passDeadline"
    ]),

    paginationPageAmount() {
      return window.innerWidth > 570 ? 5 : 2;
    },
    header() {
      return {
        quota: {
          count: this.staffPassesAmount,
          total: this.availableStaffPassesAmount
        },
        deadline: this.passDeadline.staff,
        deadlineStartPrint: this.passDeadline.startStaffPrint,
        deadlineEndPrint: this.passDeadline.endStaffPrint,
        passes: this.staffPasses
      };
    },

    isQuotaFull() {
      return this.header.quota.count >= this.header.quota.total;
    },

    availableStaffPassesAmount() {
      if (!this.event.quota) return 0;

      return this.event.quota.staff || 0;
    },
    locale() {
      return i18n.locale;
    },
    installationStartDate() {
      let date = this.event?.exhibition?.installation_started_at;
      return date ? moment(date) : null;
    },
    installationEndDate() {
      let date = this.event?.exhibition?.installation_ended_at;
      return date ? moment(date) : null;
    },
    dismantingStartDate() {
      let date = this.event?.exhibition?.dismantling_started_at;
      return date ? moment(date) : null;
    },
    dismantingEndDate() {
      let date = this.event?.exhibition?.dismantling_ended_at;
      return date ? moment(date) : null;
    },
    staffStartDate() {
      let date = this.event?.exhibition?.staff_pass_started_at;
      return date ? moment(date) : null;
    },
    staffEndDate() {
      let date = this.event?.exhibition?.staff_pass_ended_at;
      return date ? moment(date) : null;
    },
    secondsFromDeadlineStaffPrint() {
      if (!this.passDeadline || !this.passDeadline.startStaffPrint) {
        return Infinity;
      }

      return diffInSeconds(this.passDeadline.startStaffPrint);
    }
  },

  methods: {
    getLocaleValue,
    autoFillCompanyName() {
      const company = this.$store.getters["event/company"];
      if (company)
        this.companyName =
          this.locale === "en" ? company.name_eng : company.name_rus;
    },
    async updateStaffPass() {
      this.isLoading = true;
      await this.$store.dispatch("passes/fetchStaffPasses", {
        eventId: this.eventId,
        page: this.currentPage
      });
      this.isLoading = false;
    },
    async beforeOpen() {
      this.autoFillCompanyName();
      await this.updateStaffPass();
    },

    async paginate(page) {
      this.isLoadingWithOpacity = true;
      this.currentPage = page;
      await this.$store.dispatch("passes/fetchStaffPasses", {
        eventId: this.eventId,
        page: this.currentPage
      });
      this.isLoadingWithOpacity = false;
    },
    async sortingPasses() {
      this.isLoadingWithOpacity = true;
      await this.$store.dispatch("passes/fetchStaffPasses", {
        eventId: this.eventId,
        page: this.currentPage
      });
      this.isLoadingWithOpacity = false;
    },
    async uploadPackage(file) {
      try {
        this.isLoading = true;
        await this.$store.dispatch("passes/uploadStaffPasses", {
          eventId: this.event.id,
          file
        });
      } catch (error) {
        this.$store.dispatch("common/setErrorMessage", getError(error));
      } finally {
        this.isAddFormShown = false;
        this.isLoading = false;
      }
    },

    submit() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.storeAndClear();
        }
      });
    },

    async storeAndClear() {
      this.isAddFormSubmitting = true;
      const pass = {
        company_name: this.companyName,
        lastname: this.lastName,
        name: this.firstName,
        middlename: this.middleName
      };
      await this.$store
        .dispatch("passes/addStaffPass", {
          eventId: this.event.id,
          pass
        })
        .then(() => {
          this.companyName = "";
          this.lastName = "";
          this.firstName = "";
          this.middleName = "";
          this.$validator.reset();
          this.$validator.pause();

          this.isAddFormShown = false;

          this.autoFillCompanyName();
        })
        .catch(error => {
          this.$store.dispatch("common/setErrorMessage", getError(error));
        })
        .finally(() => {
          this.isAddFormSubmitting = false;
        });
    },

    async deletePass(pass) {
      this.isLoadingWithOpacity = true;
      await this.$store.dispatch("passes/removeStaffPass", {
        eventId: this.eventId,
        passId: pass.id
      });
      this.isLoadingWithOpacity = false;
    },

    async deleteAllPasses() {
      this.isLoading = true;
      await this.$store.dispatch("passes/removeAllStaffPasses", {
        eventId: this.eventId
      });
      this.isLoading = false;
    },

    async downloadPass(pass, print = false) {
      try {
        this.isLoading = true;
        const response = await apiPersonalPass.downloadPersonalPass(
          this.eventId,
          pass.id
        );
        let blob = new Blob([response], {
          type: "application/pdf"
        });
        const url = window.URL.createObjectURL(blob);
        if (print) {
          printJS(url);
        } else {
          let link = document.createElement("a");
          link.href = url;
          link.download = `Пропуск технического персонала ${pass.name} ${pass.lastname} (${pass.id}).pdf`;
          link.click();
        }
      } catch (e) {
        await this.$store.dispatch("common/setErrorMessage", getError(e));
      } finally {
        this.isLoading = false;
      }
    },

    printPass(pass) {
      this.downloadPass(pass, true);
    },

    async downloadAllPasses(print = false) {
      try {
        this.isLoading = true;

        const response = await apiPersonalPass.downloadAllPersonalPass(
          this.eventId,
          "staff"
        );
        if (response) {
          let blob = new Blob([response], {
            type: "application/pdf"
          });
          const url = window.URL.createObjectURL(blob);
          if (print) {
            printJS(url);
          } else {
            let link = document.createElement("a");
            link.href = url;
            link.download = `Пакет пропусков технического персонала.pdf`;
            link.click();
          }
        }
      } catch (e) {
        await this.$store.dispatch("common/setErrorMessage", getError(e));
      } finally {
        this.isLoading = false;
      }
    },

    printAllPasses() {
      this.downloadAllPasses(true);
    },

    diffInSeconds,

    addNewPass() {
      this.isAddFormShown = true;
      this.$nextTick(() => {
        this.$validator.reset();
        this.$validator.resume();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";

.event-modal-staff-passes {
  &__add {
    background: rgba(#f9f9f9, 0.5);
  }

  &__add + &__users:not(:empty) {
    margin-top: 40px;
    margin-bottom: 50px;
  }
  &__pagination {
    margin: 30px auto 30px;
    @include respond-down(lg) {
      margin: 20px auto 20px;
    }
  }
  &__users {
    padding-left: 80px;
    width: 780px;

    @include respond-down(lg) {
      width: 100%;
      padding: 0 24px 31px 24px;
      box-sizing: border-box;
    }

    @include respond-down(md) {
      width: 100%;
      padding: 0 0 31px 0;
      box-sizing: border-box;
    }
  }
}
.loader-wrap {
  position: relative;
  min-height: 200px;
  max-width: 870px;
}
</style>
